import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthSession, AuthUser, LoginCredentials } from '@/types/auth.types'
import type { PermissionKey } from '@/types/permission.types'
import { STORAGE_KEYS } from '@/constants/app'
import { canAccess } from '@/utils/permission.utils'
import { authService } from '@/modules/auth/services/auth.service'
import { tokenStorage } from '@/services/auth/token-storage'
import { resetUserScopedClientState } from '@/lib/session-reset'
import { cleanupFcmOnLogout } from '@/modules/notifications/utils/fcm-lifecycle'
import { resetLockState } from '@/modules/auth/lock/idle-lock'
import {
  removeTrustedAccount,
  saveTrustedAccount,
  setLastEmail,
  type TrustedAccount,
} from '@/lib/trusted-device'

interface AuthState {
  session: AuthSession | null
  isAuthenticated: boolean
  isHydrated: boolean
  rememberMe: boolean
  setHydrated: (value: boolean) => void
  login: (credentials: LoginCredentials) => Promise<AuthUser>
  /** PIN sign-in with a trusted account tile. */
  pinLogin: (account: TrustedAccount, pin: string) => Promise<AuthUser>
  /** forgetDevice = also remove this device's trust (next time: password). */
  logout: (opts?: { forgetDevice?: boolean }) => Promise<void>
  /** Clear local session without calling the API (used by 401 interceptor). */
  clearSession: () => void
  setSession: (session: AuthSession | null) => void
  updateUser: (user: Partial<AuthUser>) => void
  hydrateFromToken: () => Promise<void>
  hasPermission: (key: PermissionKey) => boolean
  hasRole: (...roles: AuthUser['role'][]) => boolean
  isSessionExpired: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      isAuthenticated: false,
      isHydrated: false,
      rememberMe: false,

      setHydrated: (value) => set({ isHydrated: value }),

      login: async (credentials) => {
        const { session, deviceToken } = await authService.login(credentials)
        const u = session.user
        if (deviceToken) {
          saveTrustedAccount({
            userId: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            avatarUrl: u.avatarUrl,
            token: deviceToken,
            remember: Boolean(credentials.remember),
          })
        } else {
          removeTrustedAccount(u.id)
        }
        // Remember the email for next time unless this is a shared computer
        // (trust unticked) — then leave nothing behind.
        setLastEmail(credentials.trustDevice ? u.email : null)
        // Clear old before the new session lands — a stale-token window
        // right before this login could have fetched into the same cache.
        resetUserScopedClientState()
        resetLockState()
        set({
          session,
          isAuthenticated: true,
          rememberMe: Boolean(credentials.remember),
        })
        return session.user
      },

      pinLogin: async (account, pin) => {
        const { session, deviceToken } = await authService.pinLogin({
          userId: account.userId,
          token: account.token,
          pin,
          remember: account.remember,
        })
        const u = session.user
        saveTrustedAccount({
          ...account,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          avatarUrl: u.avatarUrl,
          token: deviceToken,
        })
        resetUserScopedClientState()
        resetLockState()
        set({ session, isAuthenticated: true, rememberMe: account.remember })
        return u
      },

      clearSession: () => {
        tokenStorage.clear()
        resetUserScopedClientState()
        resetLockState()
        set({ session: null, isAuthenticated: false, rememberMe: false })
      },

      logout: async ({ forgetDevice = false } = {}) => {
        const userId = get().session?.user.id
        // FCM cleanup while access token is still valid; never block logout.
        try {
          await cleanupFcmOnLogout()
        } catch {
          // ignore
        }

        const refreshToken = tokenStorage.getRefreshToken()
        try {
          if (tokenStorage.getAccessToken()) {
            await authService.logout(refreshToken, forgetDevice)
          }
        } catch {
          // Still clear locally even if the network call fails
        }
        if (forgetDevice && userId) removeTrustedAccount(userId)
        get().clearSession()
      },

      setSession: (session) =>
        set({ session, isAuthenticated: Boolean(session) }),

      updateUser: (partial) => {
        const current = get().session
        if (!current) return
        set({
          session: {
            ...current,
            user: { ...current.user, ...partial },
          },
        })
      },

      hydrateFromToken: async () => {
        tokenStorage.hydrate()
        const access = tokenStorage.getAccessToken()
        if (!access) {
          set({
            session: null,
            isAuthenticated: false,
            rememberMe: tokenStorage.getRemember(),
          })
          return
        }

        try {
          const user = await authService.me()
          set({
            session: {
              user,
              authenticatedAt:
                get().session?.authenticatedAt ?? new Date().toISOString(),
              expiresAt: tokenStorage.getExpiresAt(),
            },
            isAuthenticated: true,
            rememberMe: tokenStorage.getRemember(),
          })
        } catch {
          const refreshed = await tokenStorage.refreshAccessToken()
          if (!refreshed?.accessToken) {
            get().clearSession()
            return
          }
          try {
            const user = await authService.me()
            set({
              session: {
                user,
                authenticatedAt: new Date().toISOString(),
                expiresAt: refreshed.expiresAt,
              },
              isAuthenticated: true,
              rememberMe: tokenStorage.getRemember(),
            })
          } catch {
            get().clearSession()
          }
        }
      },

      hasPermission: (key) => {
        const session = get().session
        if (!session) return false
        return canAccess(session.user.role, session.user.permissions, key)
      },

      hasRole: (...roles) => {
        const role = get().session?.user.role
        if (!role) return false
        return roles.includes(role)
      },

      isSessionExpired: () => {
        const expiresAt =
          get().session?.expiresAt ?? tokenStorage.getExpiresAt()
        if (!expiresAt) return false
        return Date.parse(expiresAt) <= Date.now()
      },
    }),
    {
      name: STORAGE_KEYS.AUTH_SESSION,
      partialize: (state) => ({
        session: state.session
          ? {
              user: state.session.user,
              authenticatedAt: state.session.authenticatedAt,
              expiresAt: state.session.expiresAt,
            }
          : null,
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe,
      }),
      onRehydrateStorage: () => () => {
        // AuthHydrationGate completes hydration after token + /me
      },
    },
  ),
)
